import { Client } from "./_clientService";

const executeRequest = async (options: any) => {
    try {
        const resp = await new Client(options)[options.method]();
        return resp;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new user
 * @param {Object} params - User data (username, email, role)
 */
async function createUser(params: any) {
    return executeRequest({
        path: "/user/create",
        payload: params,
        method: "post",
    });
}

/**
 * Get user info by ID
 * @param {string} userId - User ID
 */
async function getUserInfo(userId: any) {
    if (!userId) throw new Error("No user id provided");

    return executeRequest({
        path: `/user/info/${userId}`,
        method: "get",
    });
}

/**
 * List users with pagination and search
 * @param {Object} params - Query parameters
 */
async function listUsers(params: any) {
    let payload = {
        pageNo: params?.pageNo || 1,
        pageSize: params?.pageSize || 10,
        sortDirection: params?.sortDirection || -1,
        sortBy: params?.sortBy || "createdAt",
    };

    if (params?.search) payload["search"] = params.search;
    if (params?.skipPagination) payload["skipPagination"] = true;

    const urlParams = new URLSearchParams(payload).toString();

    return executeRequest({
        path: `/user/list?${urlParams}`,
        method: "get",
    });
}

/**
 * Update user info
 * @param {string} userId - User ID
 * @param {Object} params - Updated user data
 */
async function updateUser(userId: any, params: any) {
    if (!userId) throw new Error("No user id provided");

    return executeRequest({
        path: `/user/update/${userId}`,
        payload: params,
        method: "patch",
    });
}

/**
 * Delete user by ID
 * @param {string} userId - User ID
 */
async function deleteUser(userId: any) {
    if (!userId) throw new Error("No user id provided");

    return executeRequest({
        path: `/user/delete/${userId}`,
        method: "delete",
    });
}

/**
 * Force password reset for a user
 * @param {string} userId - User ID
 */
async function forcePasswordReset(userId: any) {
    if (!userId) throw new Error("No user id provided");

    return executeRequest({
        path: `/user/force-password-reset/${userId}`,
        method: "post",
    });
}

/**
 * Update user role
 * @param {string} userId - User ID
 * @param {string} role - New role
 */
async function updateUserRole(userId: any, role: any) {
    if (!userId) throw new Error("No user id provided");
    if (!role) throw new Error("No role provided");

    return executeRequest({
        path: `/user/update/${userId}`,
        payload: { role },
        method: "patch",
    });
}

/**
 * Update user status
 * @param {string} userId - User ID
 * @param {boolean} isActive - Active status
 */
async function updateUserStatus(userId: any, isActive: any) {
    if (!userId) throw new Error("No user id provided");
    if (typeof isActive !== "boolean") throw new Error("isActive must be a boolean");

    return executeRequest({
        path: `/user/update/${userId}`,
        payload: { isActive },
        method: "patch",
    });
}

/**
 * Request password reset email
 * @param {string} email - User email address
 */
async function forgotPassword(email: any) {
    if (!email) throw new Error("No email provided");

    return executeRequest({
        path: `/user/forgot-password`,
        payload: { email },
        method: "post",
    });
}

/**
 * Reset user password
 * @param {Object} params - Reset password data (email, newPassword)
 */
async function resetPassword(params: any) {
    if (!params.email) throw new Error("No email provided");
    if (!params.newPassword) throw new Error("No new password provided");

    return executeRequest({
        path: `/user/reset-password`,
        payload: params,
        method: "post",
    });
}

export {
    createUser,
    getUserInfo,
    listUsers,
    updateUser,
    deleteUser,
    forcePasswordReset,
    updateUserRole,
    updateUserStatus,
    forgotPassword,
    resetPassword,
};
